import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Country, europeanCountries, getCountryByCode } from "./countries";

interface CountryContextType {
  country: Country;
  setCountry: (c: Country) => void;
  detecting: boolean;
}

const CountryContext = createContext<CountryContextType | null>(null);

// ── Shared geo cache: called once, reused by countryContext + pageTracking ──
let geoPromise: Promise<{ country_code?: string; country_name?: string }> | null = null;

export function getGeoData(): Promise<{ country_code?: string; country_name?: string }> {
  if (geoPromise) return geoPromise;

  // Check sessionStorage first to avoid repeat API calls across navigations
  const cached = sessionStorage.getItem("vt_geo");
  if (cached) {
    geoPromise = Promise.resolve(JSON.parse(cached));
    return geoPromise;
  }

  geoPromise = fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) })
    .then((r) => {
      if (!r.ok) throw new Error("geo-api failed");
      return r.json();
    })
    .then((data) => {
      const result = { country_code: data?.country_code, country_name: data?.country_name };
      try { sessionStorage.setItem("vt_geo", JSON.stringify(result)); } catch { /* quota */ }
      return result;
    })
    .catch(() => ({}));

  return geoPromise;
}

export function CountryProvider({ children }: { children: ReactNode }) {
  const [country, setCountryState] = useState<Country>(europeanCountries[0]);
  const [detecting, setDetecting] = useState(true);

  const setCountry = (c: Country) => {
    setCountryState(c);
    localStorage.setItem("vantastika-country", c.code);
  };

  useEffect(() => {
    const saved = localStorage.getItem("vantastika-country");
    if (saved) {
      const found = getCountryByCode(saved);
      if (found) {
        setCountryState(found);
        setDetecting(false);
        return;
      }
    }

    getGeoData()
      .then((data) => {
        const code = data?.country_code;
        if (code) {
          const found = getCountryByCode(code);
          if (found) {
            setCountryState(found);
            localStorage.setItem("vantastika-country", found.code);
          }
        }
      })
      .finally(() => setDetecting(false));
  }, []);

  return (
    <CountryContext.Provider value={{ country, setCountry, detecting }}>
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error("useCountry must be used within CountryProvider");
  return ctx;
}
